import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { authApi } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import logoSvg from "@/assets/logo.svg";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    token ? "loading" : "error"
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    token ? "" : "Verifieringslänken saknar en giltig token."
  );
  const [pendingInvitationUrl, setPendingInvitationUrl] = useState<string | null>(null);

  useEffect(() => {
    // Check for pending invitation URL
    const invitationUrl = sessionStorage.getItem("pendingInvitationUrl");
    if (invitationUrl) {
      setPendingInvitationUrl(invitationUrl);
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    const verify = async () => {
      try {
        const result = await authApi.verifyEmail(token);

        if (result.success) {
          setStatus("success");
          // Clear the pending invitation URL from storage
          // (we'll use the state variable for the redirect)
        } else {
          setStatus("error");
          setErrorMessage(
            result.error?.message || "Kunde inte verifiera e-postadressen."
          );
        }
      } catch {
        setStatus("error");
        setErrorMessage("Ett oväntat fel uppstod. Försök igen.");
      }
    };

    verify();
  }, [token]);

  const handleContinue = () => {
    if (pendingInvitationUrl) {
      // Clear the stored URL
      sessionStorage.removeItem("pendingInvitationUrl");
      // Navigate to the invitation page
      // Extract the path from the full URL
      try {
        const url = new URL(pendingInvitationUrl);
        navigate(url.pathname + url.search);
      } catch {
        // If URL parsing fails, just navigate to the stored value
        navigate(pendingInvitationUrl);
      }
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={logoSvg} alt="Kvanten" className="h-10 w-auto mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {status === "loading" && (
            <>
              <div className="flex justify-center mb-6">
                <Spinner size="lg" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Verifierar din e-post...
              </h2>
              <p className="text-gray-600">Vänta medan vi bekräftar din e-postadress.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                E-posten har verifierats!
              </h2>
              <p className="text-gray-600 mb-6">
                {pendingInvitationUrl
                  ? "Din e-postadress är nu bekräftad. Fortsätt för att acceptera din inbjudan."
                  : "Din e-postadress är nu bekräftad. Du kan logga in och börja använda Kvanten."}
              </p>
              <button
                onClick={handleContinue}
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
              >
                <ArrowLeft size={20} />
                <span>{pendingInvitationUrl ? "Fortsätt till inbjudan" : "Gå till inloggning"}</span>
              </button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="text-red-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Verifieringen misslyckades
              </h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <Link
                to="/"
                className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
              >
                <ArrowLeft size={20} />
                <span>Till startsidan</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
